import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { ChevronDown } from 'lucide-react';
import { NormalSpan } from 'pages/charterQuote/components/contents/Content.style';

const AccordionContainer = styled.div`
  margin: 20px 0;
`;

const AccordionItem = styled.div`
  border-bottom: 1px solid #e8e8e8;
  margin-bottom: 0;
`;

const AccordionHeader = styled.div`
  padding: 16px 0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 18px;
  color: #050a30;
  transition: all 0.2s ease;

  .arrow {
    transition: transform 0.2s ease;
    transform: rotate(${(props) => (props.isOpen ? '180deg' : '0deg')});
    color: #666;
    font-size: 12px;
    font-weight: normal;
    line-height: 1;
  }
`;

const AccordionContent = styled.div`
  height: ${(props) => (props.isOpen ? props.contentHeight + 'px' : '0')};
  overflow: hidden;
  transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  .content {
    padding: 0 0 12px 0;
  }
`;

const FAQAccordion = ({ QuestionLists }) => {
  const [openIndex, setOpenIndex] = useState(0);
  const [contentHeights, setContentHeights] = useState({});
  const contentRefs = useRef({});
  const recalcHeightsRef = useRef(null);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  useLayoutEffect(() => {
    const recalcHeights = () => {
      const heights = {};
      Object.keys(contentRefs.current).forEach((key) => {
        const el = contentRefs.current[key];
        if (el) {
          heights[key] = el.scrollHeight;
        }
      });
      setContentHeights(heights);
    };

    recalcHeightsRef.current = recalcHeights;
    const id = requestAnimationFrame(recalcHeights);
    window.addEventListener('resize', recalcHeights);

    const orientationMql = window.matchMedia('(orientation: portrait)');
    const onOrientationChange = () => recalcHeights();
    if (orientationMql && orientationMql.addEventListener) {
      orientationMql.addEventListener('change', onOrientationChange);
    }

    if (document && document.fonts && document.fonts.ready) {
      document.fonts.ready.then(recalcHeights).catch(() => {});
    }

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        requestAnimationFrame(recalcHeights);
      });
      Object.keys(contentRefs.current).forEach((key) => {
        const el = contentRefs.current[key];
        if (el) ro.observe(el);
      });
    }

    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', recalcHeights);
      if (orientationMql && orientationMql.removeEventListener) {
        orientationMql.removeEventListener('change', onOrientationChange);
      }
      if (ro) ro.disconnect();
    };
  }, []);

  useEffect(() => {
    if (recalcHeightsRef.current) {
      const id = requestAnimationFrame(recalcHeightsRef.current);
      return () => cancelAnimationFrame(id);
    }
  }, [openIndex]);

  return (
    <AccordionContainer>
      {QuestionLists.map((item, index) => (
        <AccordionItem key={index}>
          <AccordionHeader onClick={() => toggleAccordion(index)} isOpen={openIndex === index}>
            <span>{item.question}</span>
            <ChevronDown className="arrow" />
          </AccordionHeader>
          <AccordionContent isOpen={openIndex === index} contentHeight={contentHeights[index] || 0}>
            <div className="content" ref={(el) => (contentRefs.current[index] = el)}>
              <NormalSpan>{item.answer}</NormalSpan>
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </AccordionContainer>
  );
};

export default FAQAccordion;
